/**
 * SWISS School Portal - Login Page
 * =================================
 * Two sign-in paths, both via Cognito Hosted UI:
 *   - "Continue with Email"  → school admins (Cognito-native, email + password)
 *   - "Continue with Google" → MSS staff (federated through Google)
 *
 * Plus demo sign-in, which mints a real Cognito session for one of
 * three seeded roles so prospects can poke around without an account.
 *
 * Layout follows the unified redesign (turn 1): a single centred
 * card, brand bar above, crisis support and contact below. The demo
 * roles sit behind the "Explore the demo portal" disclosure — the
 * design shows one link there, but all three roles are real and
 * distinct, so they're revealed on demand rather than dropped.
 * The same panel also links to Test School's public wellbeing page,
 * which needs no sign-in at all.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Phone, ArrowRight, Loader, Mail, Clock, Network,
  School as SchoolIcon, UserCog, Globe,
} from 'lucide-react';
import { CRISIS_HOTLINE, CRISIS_HOTLINE_TEL, SUPPORT_EMAIL } from '../config/navigation';
import './LoginPage.css';

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" />
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" />
  </svg>
);

const DEMO_ROLES = [
  {
    key: 'group',
    icon: Network,
    title: 'Group Admin',
    blurb: 'Aggregate view across schools',
    hint: 'Sign in as a Group Admin — the aggregate view across all schools in a group',
  },
  {
    key: 'school',
    icon: SchoolIcon,
    title: 'School Admin',
    blurb: 'One school, aggregate only',
    hint: 'Sign in as a School Admin — Test School, aggregate-only view',
  },
  {
    key: 'counsellor',
    icon: UserCog,
    title: 'School Counsellor',
    blurb: 'Per-student check-in results',
    hint: 'Sign in as the School Counsellor — same school but with per-student access',
  },
];

// The parent-facing page a school publishes at /school/:slug. Test
// School's page is opted in (publicPage.enabled) with demo content,
// so prospects can see it without signing in.
const DEMO_PUBLIC_SCHOOL_SLUG = 'test-school-demo';

const LoginPage = () => {
  const navigate = useNavigate();
  const { demoLoginAs, signInWithEmail, signInWithGoogle, error } = useAuth();

  const [loginError, setLoginError] = useState('');
  const [emailRedirecting, setEmailRedirecting] = useState(false);
  const [googleRedirecting, setGoogleRedirecting] = useState(false);
  // The single button currently mid-login (one of 'group' / 'school'
  // / 'counsellor'), so the others can dim while it works.
  const [demoRoleLoading, setDemoRoleLoading] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  // Set by adminApi when an in-app request returns 401. We show a
  // softer "your session expired" banner instead of leaving the user
  // wondering why they were bounced here. Cleared on first read so a
  // page refresh doesn't keep showing it forever.
  const [expiredNotice, setExpiredNotice] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem('swiss_login_reason') === 'expired') {
        setExpiredNotice(true);
        sessionStorage.removeItem('swiss_login_reason');
      }
    } catch { /* sessionStorage unavailable — that's fine */ }
  }, []);

  const handleEmailSignIn = async () => {
    setLoginError('');
    setEmailRedirecting(true);
    await signInWithEmail();
    setEmailRedirecting(false);
  };

  const handleGoogleSignIn = async () => {
    setLoginError('');
    setGoogleRedirecting(true);
    await signInWithGoogle();
    setGoogleRedirecting(false);
  };

  // One-click "Sign in as <role>" — uses Cognito's USER_PASSWORD_AUTH
  // directly so no Hosted UI redirect is involved.
  const handleDemoLoginAs = async (role) => {
    setLoginError('');
    setDemoRoleLoading(role);
    try {
      const result = await demoLoginAs(role);
      if (result.success) {
        // Group admin → /admin/systems/:id, school admin / counsellor → /dashboard
        if (result.role === 'system_admin' && result.systemId) {
          navigate(`/admin/systems/${result.systemId}`);
        } else {
          navigate('/dashboard');
        }
      } else {
        setLoginError(result.error || 'Demo sign-in failed.');
      }
    } finally {
      setDemoRoleLoading(null);
    }
  };

  return (
    <div className="login-page">
      <header className="login-bar">
        <img
          src={`${process.env.PUBLIC_URL}/SWISS_Logo_4.webp`}
          alt="SWISS · Student Wellness & Institutional Safety Standards"
          className="login-bar-logo"
        />
        <span className="login-bar-title">SWISS School Portal</span>
      </header>

      <main className="login-main">
        <div className="login-card">
          <div className="login-card-head">
            <h1 className="login-title">Sign in</h1>
            <p className="login-subtitle">Access your school&apos;s dashboard</p>
          </div>

          {expiredNotice && !loginError && !error && (
            <div className="login-notice">
              <Clock size={15} aria-hidden="true" />
              <span>Your session expired. Please sign in again to continue.</span>
            </div>
          )}
          {(loginError || error) && (
            <div className="login-error" role="alert">{loginError || error}</div>
          )}

          <button
            type="button"
            className="login-btn primary"
            onClick={handleEmailSignIn}
            disabled={emailRedirecting}
          >
            {emailRedirecting ? (
              <><Loader size={16} className="spin" aria-hidden="true" />Redirecting…</>
            ) : (
              <><Mail size={16} aria-hidden="true" />Continue with Email</>
            )}
          </button>

          <button
            type="button"
            className="login-btn secondary"
            onClick={handleGoogleSignIn}
            disabled={googleRedirecting}
          >
            {googleRedirecting ? (
              <><Loader size={16} className="spin" aria-hidden="true" />Redirecting to Google…</>
            ) : (
              <><GoogleGlyph />Continue with Google</>
            )}
          </button>

          <p className="login-hint">School admins use email · MSS staff use Google</p>

          <div className="login-demo">
            {!demoOpen ? (
              <>
                <span className="login-demo-lead">New here?</span>
                <button
                  type="button"
                  className="login-demo-toggle"
                  onClick={() => setDemoOpen(true)}
                  aria-expanded="false"
                >
                  Explore the demo portal <ArrowRight size={14} aria-hidden="true" />
                </button>
              </>
            ) : (
              <div className="login-demo-panel">
                <div className="login-demo-head">
                  <span>Pick a role to explore</span>
                  <button
                    type="button"
                    className="login-demo-close"
                    onClick={() => setDemoOpen(false)}
                    aria-expanded="true"
                  >
                    Cancel
                  </button>
                </div>

                {DEMO_ROLES.map((role) => (
                  <button
                    key={role.key}
                    type="button"
                    className="login-demo-role"
                    onClick={() => handleDemoLoginAs(role.key)}
                    disabled={!!demoRoleLoading}
                    title={role.hint}
                  >
                    {demoRoleLoading === role.key
                      ? <Loader size={16} className="spin" aria-hidden="true" />
                      : <role.icon size={16} aria-hidden="true" />}
                    <span>
                      <strong>{role.title}</strong>
                      <small>{role.blurb}</small>
                    </span>
                  </button>
                ))}

                <Link
                  to={`/school/${DEMO_PUBLIC_SCHOOL_SLUG}`}
                  className="login-demo-role"
                  title="See the public wellbeing page a school shares with parents — no sign-in"
                >
                  <Globe size={16} aria-hidden="true" />
                  <span>
                    <strong>Public School Page</strong>
                    <small>What parents see · no sign-in</small>
                  </span>
                </Link>
              </div>
            )}
          </div>

          <p className="login-forgot">
            Forgot your password? On the next screen, choose “Forgot password”,
            or contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </div>
      </main>

      <footer className="login-footer">
        <a className="login-footer-crisis" href={`tel:${CRISIS_HOTLINE_TEL}`}>
          <Phone size={13} aria-hidden="true" />
          Crisis Support · {CRISIS_HOTLINE}
        </a>
        <span className="login-footer-sep" aria-hidden="true">·</span>
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </footer>
    </div>
  );
};

export default LoginPage;
